import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CalendarDays, ChevronDown, X } from 'lucide-react';
import type { DateRangePreset } from '../../lib/dateFilter';
import { useIsMobile } from '../../lib/useIsMobile';
import { useFinancialYear } from '../../lib/financialYear';

/**
 * Reusable date-range control: a quick-preset dropdown plus two From/To calendar
 * pickers. Mobile-safe by design — everything lives in a 2-column grid that
 * never overflows the viewport, and clearing is done via one dedicated button
 * (the date inputs themselves have no inline clear "x").
 */
export interface DateRangeFilterProps {
  start: Date | null;
  end: Date | null;
  preset: DateRangePreset;
  onStart: (d: Date | null) => void;
  onEnd: (d: Date | null) => void;
  onPreset: (p: DateRangePreset) => void;
  onClear: () => void;
  className?: string;
  compact?: boolean;
}

const PRESETS: { id: DateRangePreset; label: string }[] = [
  { id: 'all', label: 'All time' },
  { id: 'today', label: 'Today' },
  { id: 'this_week', label: 'This week' },
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'this_quarter', label: 'This quarter' },
  { id: 'this_year', label: 'Calendar year' },
  { id: 'financial_year', label: 'Financial year' },
  { id: 'custom', label: 'Custom range' },
];

const fieldBox =
  'flex h-10 min-w-0 items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 shadow-sm transition focus-within:border-emerald-500/60 focus-within:ring-2 focus-within:ring-emerald-500/15';

export default function DateRangeFilter({
  start,
  end,
  preset,
  onStart,
  onEnd,
  onPreset,
  onClear,
  className = '',
}: DateRangeFilterProps) {
  const isFiltered = preset !== 'all' || !!start || !!end;
  const isMobile = useIsMobile();
  const fy = useFinancialYear();

  const pickerInput = 'h-full w-full min-w-0 border-0 bg-transparent p-0 text-sm font-medium text-zinc-800 outline-none placeholder:text-zinc-400 focus:ring-0';

  return (
    <div
      className={`grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center sm:justify-end ${className}`}
    >
      {/* Quick preset — full width on mobile */}
      <div className="relative col-span-2 sm:col-span-1 sm:w-44">
        <CalendarDays className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-600" />
        <select
          value={preset}
          onChange={(e) => onPreset(e.target.value as DateRangePreset)}
          className="h-10 w-full appearance-none rounded-lg border border-zinc-200 bg-white pl-9 pr-8 text-sm font-semibold text-zinc-800 shadow-sm outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/15"
          aria-label="Quick date range"
        >
          {PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.id === 'financial_year' ? fy.selected.label : p.label}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
      </div>

      {/* From + To — kept side by side on every breakpoint */}
      <div className="col-span-2 grid grid-cols-2 gap-2 sm:col-span-1 sm:flex sm:items-center">
        <div className={`${fieldBox} sm:w-40`}>
          <span className="hidden shrink-0 text-[10px] font-bold uppercase tracking-wide text-zinc-400 sm:inline">From</span>
          <DatePicker
            selected={start}
            onChange={onStart}
            maxDate={end ?? new Date()}
            dateFormat="dd MMM yyyy"
            placeholderText="From date"
            className={pickerInput}
            wrapperClassName="w-full min-w-0"
            showPopperArrow={false}
            withPortal={isMobile}
            calendarClassName="!rounded-xl !border !border-zinc-200 !font-sans !shadow-lg"
          />
        </div>

        <div className={`${fieldBox} sm:w-40`}>
          <span className="hidden shrink-0 text-[10px] font-bold uppercase tracking-wide text-zinc-400 sm:inline">To</span>
          <DatePicker
            selected={end}
            onChange={onEnd}
            minDate={start ?? undefined}
            maxDate={new Date()}
            dateFormat="dd MMM yyyy"
            placeholderText="To date"
            className={pickerInput}
            wrapperClassName="w-full min-w-0"
            showPopperArrow={false}
            withPortal={isMobile}
            calendarClassName="!rounded-xl !border !border-zinc-200 !font-sans !shadow-lg"
          />
        </div>
      </div>

      {isFiltered && (
        <button
          type="button"
          onClick={onClear}
          className="col-span-2 inline-flex h-10 items-center justify-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 text-xs font-semibold text-zinc-600 shadow-sm hover:bg-zinc-50 sm:col-span-1 sm:w-auto"
        >
          <X className="h-3.5 w-3.5" />
          Clear dates
        </button>
      )}
    </div>
  );
}
